import React from 'react';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ProgressArc } from '../ProgressArc/ProgressArc';

// --- TYPE DEFINITIONS ---

type KpiColor = 'blue' | 'green' | 'red' | 'orange' | 'purple';

interface KPICardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: number;
  total: number;
  percentage: number;
  subtitle: string;
  variant?: 'card' | 'horizontal';
  color?: KpiColor;
  onClick?: (data: Omit<KPICardProps, 'onClick'>) => void;
}

// --- CONSTANTS ---

const PROGRESS_SIZE = {
  card: 96,
  horizontal: 64,
};
const PROGRESS_STROKE_WIDTH = {
  card: 6,
  horizontal: 4,
};

const KPI_COLOR_VARIANTS: Record<KpiColor, 'default' | 'success' | 'error' | 'warning' | 'secondary'> = {
  blue: 'default',
  green: 'success',
  red: 'error',
  orange: 'warning',
  purple: 'secondary',
};

const KPI_TEXT_COLORS: Record<KpiColor, { percentage: string; dot: string }> = {
  blue: { percentage: 'text-cyan-600', dot: 'bg-cyan-500' },
  green: { percentage: 'text-emerald-600', dot: 'bg-emerald-500' },
  red: { percentage: 'text-rose-600', dot: 'bg-rose-500' },
  orange: { percentage: 'text-orange-600', dot: 'bg-orange-500' },
  purple: { percentage: 'text-violet-600', dot: 'bg-violet-500' },
};

// --- MAIN COMPONENT ---

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  total,
  percentage,
  subtitle,
  variant = 'card',
  color = 'blue',
  onClick,
  className = '',
  ...props
}) => {
  const colorTheme = KPI_TEXT_COLORS[color];
  const progressVariant = KPI_COLOR_VARIANTS[color];
  const isCard = variant === 'card';

  const handleClick = () => {
    if (onClick) {
      onClick({ title, value, total, percentage, subtitle, variant, color, className });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  if (isCard) {
    return (
      <Card
        className={cn('p-6', onClick && 'cursor-pointer hover:bg-gray-50', className)}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={onClick ? 0 : -1}
        role={onClick ? 'button' : 'region'}
        aria-label={onClick ? `${title} KPI card` : undefined}
        {...props}
      >
        <CardHeader className="p-0 mb-4">
          <div className="flex items-center justify-between">
            <CardTitle>{title}</CardTitle>
            <span className={cn('text-body font-semibold', colorTheme.percentage)}>{percentage}%</span>
          </div>
        </CardHeader>
        <CardContent className="p-0 flex justify-center mb-4">
          <ProgressArc
            variant={progressVariant}
            value={value}
            max={total}
            size={PROGRESS_SIZE.card}
            strokeWidth={PROGRESS_STROKE_WIDTH.card}
          />
        </CardContent>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-800 mb-1">{value} / {total}</p>
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500">
            <span className={cn('w-2 h-2 rounded-full', colorTheme.dot)} />
            <span>{subtitle}</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn('p-4 flex items-center gap-4', onClick && 'cursor-pointer hover:bg-gray-50', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : -1}
      role={onClick ? 'button' : 'region'}
      aria-label={onClick ? `${title} KPI card` : undefined}
      {...props}
    >
      <CardContent className="flex-1 p-0">
        <CardTitle className="mb-1">{title}</CardTitle>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span className={cn('w-2 h-2 rounded-full', colorTheme.dot)} />
          <span>{subtitle}</span>
        </div>
      </CardContent>
      <div className="flex-shrink-0">
        <ProgressArc
          variant={progressVariant}
          value={value}
          max={total}
          size={PROGRESS_SIZE.horizontal}
          strokeWidth={PROGRESS_STROKE_WIDTH.horizontal}
        />
      </div>
    </Card>
  );
};

KPICard.displayName = 'KPICard';

export { KPICard };
export default KPICard;
export type { KPICardProps };
